import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { RestrictionsGroupService } from '@/services/RestrictionsGroupService';

import { CardService } from '@/features/cards/services/CardService';

import { RestrictionsGroupMetadataService } from '@/services/RestrictionsGroupMetadataService';
import { TAppState } from '@/store/reducers/reducers';
import { ICursor, IPagination } from '@/store/types/search.types';
import {
  IRestrictionsGroup,
  IRestrictionsGroupMetadata,
  TRestrictionsGroupMetadataValue,
  RestrictionsGroupStatus,
  TRestrictionsGroupType,
  restrictionsGroupIdTypes,
  ISearchPredefRestrictionsGroups,
} from '@/store/models/restriction.model';

import { TCoreAction } from '@/store/types/modals.types';
import { TAppError } from '@/store/types/base.state';
import {
  TPredefRestrictionsGroupAction,
  PredefRestrictionsGroupActionTypes,
} from '@/store/types/predef-restrictions-groups.types';

import { RestrictionsTools } from '@/utils/tools/RestrictionsTools';

import { closeModal } from '@/store/actions/core.actions';

export const searchPredefRestrictionsGroups = ({
  types,
  groupId,
  currentPage = 1,
  pageCount = 50,
  filters = [],
}: ISearchPredefRestrictionsGroups): ThunkAction<Promise<void>, TAppState, {}, TPredefRestrictionsGroupAction> => {
  const request = (type: TRestrictionsGroupType): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.SEARCH_PREDEF_RESTRICTIONS_GROUPS,
    restrictionsGroupType: type,
  });
  const fail = (type: TRestrictionsGroupType, error: TAppError): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.SEARCH_PREDEF_RESTRICTIONS_GROUPS_FAIL,
    error,
    restrictionsGroupType: type,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#list-based-restrictions',
  });
  const success = (
    restrictions: IRestrictionsGroup[],
    type: TRestrictionsGroupType,
    pagination: IPagination,
  ): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.SEARCH_PREDEF_RESTRICTIONS_GROUPS_SUCCESS,
    shouldSkipToaster: true,
    restrictionsGroupType: type,
    restrictionsGroups: restrictions,
    pagination,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TPredefRestrictionsGroupAction>): Promise<void> => {
    await Promise.all(
      types.map(async (type) => {
        try {
          dispatch(request(type));
          const { meta, [restrictionsGroupIdTypes[type]]: restrictionsGroupResponse } =
            await RestrictionsGroupService.searchRestrictionsGroups({
              type,
              restrictionsGroupId: groupId,
              currentPage,
              pageCount,
              filters: [
                {
                  fieldName: 'name',
                  criteria: 'like',
                  expression: 'PREDEF_%',
                },
                ...filters,
              ],
            });

          const restrictionGroups = restrictionsGroupResponse.map((restrictionResponse) =>
            RestrictionsTools.convertToRestrictionsGroup(restrictionResponse, type),
          );

          dispatch(
            success(restrictionGroups, type, {
              currentPage,
              totalPages: meta?.totalPages,
            }),
          );
        } catch (error) {
          dispatch(fail(type, error));
        }
      }),
    );
  };
};

export const addPredefRestrictionsGroup = (
  newRestrictionsGroup: IRestrictionsGroup,
): ThunkAction<Promise<void>, TAppState, {}, TPredefRestrictionsGroupAction | TCoreAction> => {
  const request = (): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.ADD_PREDEF_RESTRICTION_GROUP,
  });
  const fail = (error: TAppError): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.ADD_PREDEF_RESTRICTION_GROUP_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#list-based-restrictions',
  });
  const success = (restrictionsGroup: IRestrictionsGroup): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.ADD_PREDEF_RESTRICTION_GROUP_SUCCESS,
    restrictionsGroup,
  });

  return async (
    dispatch: ThunkDispatch<TAppState, {}, TPredefRestrictionsGroupAction | TCoreAction>,
  ): Promise<void> => {
    dispatch(request());
    const baaSRestrictionsGroup = RestrictionsTools.convertToBaaSRestrictionsGroup(newRestrictionsGroup);
    try {
      const baaSRestrictionsGroupAdded = await RestrictionsGroupService.addRestrictionsGroup(
        { ...baaSRestrictionsGroup, status: RestrictionsGroupStatus.VALIDATED },
        newRestrictionsGroup.type,
      );

      const restrictionsGroup = RestrictionsTools.convertToRestrictionsGroup(
        baaSRestrictionsGroupAdded,
        newRestrictionsGroup.type,
      );

      dispatch(success(restrictionsGroup));
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updatePredefRestrictionsGroup = (
  restrictionsGroup: IRestrictionsGroup,
): ThunkAction<Promise<void>, TAppState, {}, TPredefRestrictionsGroupAction | TCoreAction> => {
  const request = (): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTION_GROUP,
    restrictionsGroupType: restrictionsGroup.type,
    restrictionsGroupId: restrictionsGroup.id as number, // If update restrictions group action, id is mandatory
  });
  const fail = (error: TAppError): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTION_GROUP_FAIL,
    restrictionsGroupType: restrictionsGroup.type,
    restrictionsGroupId: restrictionsGroup.id as number, // If update restrictions group action, id is mandatory
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#list-based-restrictions',
  });
  const success = (restrictionsGroup: IRestrictionsGroup): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTION_GROUP_SUCCESS,
    restrictionsGroup,
  });

  return async (
    dispatch: ThunkDispatch<TAppState, {}, TPredefRestrictionsGroupAction | TCoreAction>,
  ): Promise<void> => {
    dispatch(request());
    const baaSRestrictionsGroup = RestrictionsTools.convertToBaaSRestrictionsGroup(restrictionsGroup);

    try {
      const baaSRestrictionsGroupUpdated = await RestrictionsGroupService.updateRestrictionsGroup(
        baaSRestrictionsGroup,
        restrictionsGroup.type,
      );

      const restrictionsGroupUpdated = RestrictionsTools.convertToRestrictionsGroup(
        baaSRestrictionsGroupUpdated,
        restrictionsGroup.type,
      );

      dispatch(success(restrictionsGroupUpdated));
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const deletePredefRestrictionsGroup = (
  restrictionsGroup: IRestrictionsGroup,
): ThunkAction<Promise<void>, TAppState, {}, TPredefRestrictionsGroupAction> => {
  const request = (): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.DELETE_PREDEF_RESTRICTION_GROUP,
  });
  const fail = (error: TAppError): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.DELETE_PREDEF_RESTRICTION_GROUP_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/modification.html#list-based-restrictions',
  });
  const success = (restrictionsGroupId: number): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.DELETE_PREDEF_RESTRICTION_GROUP_SUCCESS,
    restrictionsGroupType: restrictionsGroup.type,
    restrictionsGroupId,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TPredefRestrictionsGroupAction>): Promise<void> => {
    dispatch(request());

    try {
      if (!restrictionsGroup.id) return Promise.reject('Restrictions Group id not provided to delete it');

      await CardService.checkRestrictionLinkedToCard(restrictionsGroup.id, restrictionsGroup.type);

      const baaSRestrictionsGroupDeleted = await RestrictionsGroupService.deleteRestrictionsGroup(
        restrictionsGroup.id,
        restrictionsGroup.type,
      );

      if (!baaSRestrictionsGroupDeleted.id) return Promise.reject('Restrictions Group id not found');

      dispatch(success(baaSRestrictionsGroupDeleted.id));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getPredefRestrictionsGroupMetadata = (
  restrictionsGroupType: TRestrictionsGroupType,
  restrictionsGroupId: number,
  currentCursor?: string | null,
): ThunkAction<Promise<void>, TAppState, {}, TPredefRestrictionsGroupAction> => {
  const request = (): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.GET_PREDEF_RESTRICTIONS_GROUP_METADATA,
    restrictionsGroupType,
    restrictionsGroupId,
  });
  const fail = (error: TAppError): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.GET_PREDEF_RESTRICTIONS_GROUP_METADATA_FAIL,
    restrictionsGroupType,
    restrictionsGroupId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/mid-metadata.html#mid',
  });

  const success = (metadata: any, cursor: ICursor): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.GET_PREDEF_RESTRICTIONS_GROUP_METADATA_SUCCESS,
    shouldSkipToaster: true,
    restrictionsGroupType,
    restrictionsGroupId,
    metadata,
    cursor,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TPredefRestrictionsGroupAction>): Promise<void> => {
    dispatch(request());
    try {
      const { elements: transactions, cursor } = await RestrictionsGroupMetadataService.getRestrictionsGroupMetadata(
        restrictionsGroupId,
        currentCursor,
      );
      dispatch(success(transactions, cursor));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updatePredefRestrictionsGroupMetadataById = (
  restrictionsGroupType: TRestrictionsGroupType,
  restrictionsGroupId: number,
  restriction: number | string,
  metadataValue: TRestrictionsGroupMetadataValue,
): ThunkAction<Promise<void>, TAppState, {}, TPredefRestrictionsGroupAction> => {
  const request = (): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_SINGLE_METADATA,
    restrictionsGroupType,
    restrictionsGroupId,
  });
  const fail = (error: TAppError): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_SINGLE_METADATA_FAIL,
    restrictionsGroupType,
    restrictionsGroupId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/mid-metadata.html#mid',
  });
  const success = (metadataValue: TRestrictionsGroupMetadataValue): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_SINGLE_METADATA_SUCCESS,
    restrictionsGroupType,
    restrictionsGroupId,
    restriction,
    metadataValue,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TPredefRestrictionsGroupAction>): Promise<void> => {
    dispatch(request());
    try {
      const restrictionsGroupManagementDetailsResponse =
        await RestrictionsGroupMetadataService.updateRestrictionsGroupMetadataById(
          restrictionsGroupType,
          restriction,
          metadataValue,
        );

      dispatch(success(restrictionsGroupManagementDetailsResponse));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updateAllPredefRestrictionsGroupMetadata = (
  restrictionsGroupType: TRestrictionsGroupType,
  restrictionsGroupId: number,
  metadatas: IRestrictionsGroupMetadata[],
): ThunkAction<Promise<void>, TAppState, {}, TPredefRestrictionsGroupAction> => {
  const request = (): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_METADATA,
    restrictionsGroupType,
    restrictionsGroupId,
  });
  const fail = (error: TAppError): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_METADATA_FAIL,
    restrictionsGroupType,
    restrictionsGroupId,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/mid-metadata.html#mid',
  });
  const success = (metadatas: IRestrictionsGroupMetadata[]): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.UPDATE_PREDEF_RESTRICTIONS_GROUP_METADATA_SUCCESS,
    restrictionsGroupType,
    restrictionsGroupId,
    metadatas,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TPredefRestrictionsGroupAction>): Promise<void> => {
    dispatch(request());

    try {
      const restrictionsGroupManagementDetailsResponse =
        await RestrictionsGroupMetadataService.updateAllRestrictionsGroupMetadata(restrictionsGroupType, metadatas);

      dispatch(success(restrictionsGroupManagementDetailsResponse));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const deletePredefRestrictionsGroupMetadata = (
  restrictionsGroup: IRestrictionsGroup,
): ThunkAction<Promise<void>, TAppState, {}, TPredefRestrictionsGroupAction> => {
  return async (dispatch: ThunkDispatch<TAppState, {}, TPredefRestrictionsGroupAction>): Promise<void> => {
    if (!restrictionsGroup.id) return Promise.reject('Restrictions Group id not provided to delete it');

    dispatch(updatePredefRestrictionsGroup(restrictionsGroup));
    dispatch(getPredefRestrictionsGroupMetadata(restrictionsGroup.type, restrictionsGroup.id));
  };
};

export const saveRestrictionPath = (
  tabIdRestriction: number,
): ThunkAction<Promise<void>, TAppState, {}, TPredefRestrictionsGroupAction> => {
  const request = (tabId: number): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.SAVE_RESTRICTIONS_PATH,
    tabId,
  });
  return async (dispatch: ThunkDispatch<TAppState, {}, TPredefRestrictionsGroupAction>): Promise<void> => {
    dispatch(request(tabIdRestriction));
  };
};

export const resetPredefRestrictionsGroups = (
  types: TRestrictionsGroupType[],
): ThunkAction<Promise<void>, TAppState, {}, TPredefRestrictionsGroupAction> => {
  const reset = (type: TRestrictionsGroupType): TPredefRestrictionsGroupAction => ({
    type: PredefRestrictionsGroupActionTypes.RESET_RESTRICTIONS_GROUPS,
    restrictionGroupType: type,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TPredefRestrictionsGroupAction>): Promise<void> => {
    types.map(async (type) => {
      dispatch(reset(type));
    });
  };
};
